interface ProgressMapProps {
  currentClue: number;
  totalClues: number;
  solvedCount: number;
}

export function ProgressMap({ currentClue, totalClues, solvedCount }: ProgressMapProps) {
  const progress = (solvedCount / totalClues) * 100;

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-text-dark">
          Progress: {solvedCount} / {totalClues} clues
        </span>
        <span className="text-sm text-text-light">
          {Math.round(progress)}%
        </span>
      </div>

      <div className="relative">
        {/* Progress bar background */}
        <div className="h-3 bg-cream rounded-full overflow-hidden">
          {/* Filled progress */}
          <div
            className="h-full bg-gradient-to-r from-secondary to-primary transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Clue markers */}
        <div className="absolute inset-0 flex items-center justify-between px-1">
          {Array.from({ length: totalClues }).map((_, i) => {
            const isSolved = i < solvedCount;
            const isCurrent = i === currentClue - 1;

            return (
              <div
                key={i}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  isSolved
                    ? 'bg-primary'
                    : isCurrent
                    ? 'bg-accent ring-2 ring-accent ring-offset-1'
                    : 'bg-gray-300'
                }`}
                title={`Clue ${i + 1}${isSolved ? ' (solved)' : isCurrent ? ' (current)' : ''}`}
              />
            );
          })}
        </div>
      </div>

      {/* Visual paper fold effect */}
      <div className="mt-4 flex justify-center">
        <PaperFoldVisualization progress={progress} />
      </div>
    </div>
  );
}

function PaperFoldVisualization({ progress }: { progress: number }) {
  // Simple representation of paper unfolding
  const folds = 4;
  const unfoldedFolds = Math.floor((progress / 100) * folds);

  return (
    <div className="flex gap-1">
      {Array.from({ length: folds }).map((_, i) => {
        const isUnfolded = i < unfoldedFolds;
        const isPartial = i === unfoldedFolds && progress % 25 > 0;

        return (
          <div
            key={i}
            className={`w-8 h-8 rounded transition-all duration-500 ${
              isUnfolded
                ? 'bg-secondary rotate-0 opacity-100'
                : isPartial
                ? 'bg-secondary rotate-12 opacity-60'
                : 'bg-gray-200 rotate-45 opacity-40'
            }`}
            style={{
              transformOrigin: 'center',
            }}
          />
        );
      })}
    </div>
  );
}
